import {sendEvent} from '@github-ui/hydro-analytics'
import {createContext, type ReactNode, useContext} from 'react'

type MarkdownViewMode = 'edit' | 'preview' | 'hybrid'

type ViewSwitchTrigger = 'view_switcher' | 'keyboard_shortcut' | 'programmatic'
type ToolbarInteractionTrigger = 'toolbar' | 'keyboard'
type AutocompleteState = 'loading' | 'empty' | 'results'
type FileUploadPhase = 'selected' | 'succeeded' | 'failed'

type SuggestionKind = 'string' | 'object' | 'null'

type HybridEditorTelemetryOptions = {
  featureFlagEnabled: boolean
  editorId?: string
  surface?: string
}

type TrackViewSwitchArgs = {
  from?: MarkdownViewMode | null
  to: MarkdownViewMode
  trigger: ViewSwitchTrigger
}

type TrackToolbarInteractionArgs = {
  control: string
  action?: string
  trigger: ToolbarInteractionTrigger
  isActive?: boolean
  keybinding?: string
  handled?: boolean
}

type TrackKeyboardShortcutArgs = {
  commandId: string
  shortcut: string
  handled: boolean
}

type TrackAutocompleteShownArgs = {
  triggerChar: string
  queryLength: number
  suggestionState: AutocompleteState
  suggestionCount?: number
}

type TrackAutocompleteSelectedArgs = {
  triggerChar: string
  insertedLength: number
  multiStep: boolean
  suggestionKind: SuggestionKind
}

type TrackFileUploadArgs = {
  phase: FileUploadPhase
  mimeType: string
  sizeBytes: number
  mediaKind?: 'image' | 'video' | 'other'
  errorType?: 'upload' | 'dimension' | 'unknown'
}

export interface HybridEditorTelemetry {
  trackViewSwitch(args: TrackViewSwitchArgs): void
  trackToolbarInteraction(args: TrackToolbarInteractionArgs): void
  trackKeyboardShortcut(args: TrackKeyboardShortcutArgs): void
  trackAutocompleteShown(args: TrackAutocompleteShownArgs): void
  trackAutocompleteSelected(args: TrackAutocompleteSelectedArgs): void
  trackFileUpload(args: TrackFileUploadArgs): void
}

const EVENT_PREFIX = 'hybrid_markdown_editor'

const noopTelemetry: HybridEditorTelemetry = {
  trackViewSwitch: () => {},
  trackToolbarInteraction: () => {},
  trackKeyboardShortcut: () => {},
  trackAutocompleteShown: () => {},
  trackAutocompleteSelected: () => {},
  trackFileUpload: () => {},
}

const HybridEditorTelemetryContext = createContext<HybridEditorTelemetry>(noopTelemetry)

export function useHybridEditorTelemetry(): HybridEditorTelemetry {
  return useContext(HybridEditorTelemetryContext)
}

type HybridEditorTelemetryProviderProps = {
  telemetry: HybridEditorTelemetry
  children: ReactNode
}

export function HybridEditorTelemetryProvider({telemetry, children}: HybridEditorTelemetryProviderProps) {
  return <HybridEditorTelemetryContext.Provider value={telemetry}>{children}</HybridEditorTelemetryContext.Provider>
}

type TelemetryPropertyValue = string | number | boolean | null | undefined

export function createHybridEditorTelemetry({
  featureFlagEnabled,
  editorId,
  surface,
}: HybridEditorTelemetryOptions): HybridEditorTelemetry {
  const baseProperties: Record<string, TelemetryPropertyValue> = {
    ['feature_flag_markdown_experience_hybrid_editor']: featureFlagEnabled,
  }

  if (editorId) {
    baseProperties['editor_id'] = editorId
  }

  if (surface) {
    baseProperties.surface = surface
  }

  const sendWithBaseProperties = (eventName: string, properties: Record<string, TelemetryPropertyValue>) => {
    const payload: Record<string, TelemetryPropertyValue> = {
      ...baseProperties,
      ...properties,
    }

    try {
      sendEvent(eventName, payload)
    } catch (error) {
      if (error instanceof ReferenceError && typeof error.message === 'string' && error.message.includes('telemetry')) {
        return
      }

      throw error
    }
  }

  const trackHybridEvent = (event: string, properties: Record<string, TelemetryPropertyValue>) => {
    sendWithBaseProperties(`${EVENT_PREFIX}.${event}`, properties)
  }

  return {
    trackViewSwitch: ({from, to, trigger}) => {
      sendWithBaseProperties('markdown_editor.select_view', {
        mode: to,
        ['from_mode']: from ?? 'unknown',
        trigger,
        ['did_change']: from ? from !== to : true,
      })
    },
    trackToolbarInteraction: ({control, action, trigger, isActive, keybinding, handled}) => {
      trackHybridEvent('toolbar_interaction', {
        control,
        action,
        trigger,
        ['is_active']: isActive ?? null,
        keybinding,
        handled: handled ?? null,
      })
    },
    trackKeyboardShortcut: ({commandId, shortcut, handled}) => {
      trackHybridEvent('keyboard_shortcut', {
        command: commandId,
        shortcut,
        handled,
      })
    },
    trackAutocompleteShown: ({triggerChar, queryLength, suggestionState, suggestionCount}) => {
      trackHybridEvent('autocomplete_shown', {
        ['trigger_char']: triggerChar,
        ['query_length']: queryLength,
        ['suggestion_state']: suggestionState,
        ['suggestion_count']: suggestionCount ?? null,
      })
    },
    trackAutocompleteSelected: ({triggerChar, insertedLength, multiStep, suggestionKind}) => {
      trackHybridEvent('autocomplete_selected', {
        ['trigger_char']: triggerChar,
        ['inserted_length']: insertedLength,
        ['multi_step']: multiStep,
        ['suggestion_kind']: suggestionKind,
      })
    },
    trackFileUpload: ({phase, mimeType, sizeBytes, mediaKind, errorType}) => {
      trackHybridEvent('file_upload', {
        phase,
        ['mime_type']: mimeType,
        ['size_bytes']: sizeBytes,
        ['media_kind']: mediaKind ?? 'other',
        ['error_type']: errorType ?? null,
      })
    },
  }
}

export function createNoopHybridEditorTelemetry(): HybridEditorTelemetry {
  return noopTelemetry
}

export type {
  AutocompleteState,
  FileUploadPhase,
  HybridEditorTelemetryOptions,
  MarkdownViewMode,
  SuggestionKind,
  ToolbarInteractionTrigger,
  TrackAutocompleteSelectedArgs,
  TrackAutocompleteShownArgs,
  TrackFileUploadArgs,
  TrackKeyboardShortcutArgs,
  TrackToolbarInteractionArgs,
  TrackViewSwitchArgs,
  ViewSwitchTrigger,
}

try{ HybridEditorTelemetryContext.displayName ||= 'HybridEditorTelemetryContext' } catch {}
try{ HybridEditorTelemetryProvider.displayName ||= 'HybridEditorTelemetryProvider' } catch {}